import { forwardRef, useId, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import styles from './RadioGroup.module.css';
import type { FieldErrorLike } from './fieldUtils';

export interface RadioOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  label?: ReactNode;
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  layout?: 'horizontal' | 'vertical';
  error?: FieldErrorLike | undefined;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    name,
    label,
    options,
    value,
    defaultValue,
    onChange,
    layout = 'horizontal',
    error,
    required,
    disabled,
    className,
  },
  ref,
) {
  const reactId = useId();
  const groupId = `radio-${reactId}`;
  const errorMsg = error?.message;

  return (
    <div className={className}>
      {label !== undefined ? (
        <span className={styles.label} id={`${groupId}-label`}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden>
              *
            </span>
          ) : null}
        </span>
      ) : null}
      <div
        ref={ref}
        role="radiogroup"
        aria-labelledby={label !== undefined ? `${groupId}-label` : undefined}
        aria-invalid={errorMsg !== undefined || undefined}
        className={cn(styles.group, layout === 'vertical' ? styles.vertical : styles.horizontal)}
      >
        {options.map((opt) => {
          const isChecked =
            value !== undefined ? value === opt.value : defaultValue === opt.value;
          const isDisabled = disabled || opt.disabled;
          return (
            <label
              key={opt.value}
              className={cn(
                styles.option,
                isChecked && styles.checked,
                isDisabled && styles.optionDisabled,
              )}
            >
              <input
                type="radio"
                name={name}
                value={opt.value}
                checked={value !== undefined ? isChecked : undefined}
                defaultChecked={value === undefined ? isChecked : undefined}
                disabled={isDisabled}
                onChange={(e) => onChange?.(e.target.value)}
                className={styles.input}
              />
              <span>{opt.label}</span>
            </label>
          );
        })}
      </div>
      {errorMsg ? (
        <p className={styles.error} role="alert">
          {errorMsg}
        </p>
      ) : null}
    </div>
  );
});
